import { RefreshCw } from 'lucide-react';
import Button from '../Button/Button';
import useVersionCheck from '../../../hooks/useVersionCheck';
import './UpdatePrompt.css';

/**
 * Update prompt
 *
 * Mounted once, app-wide. Polls the backend for the live frontend build and,
 * when this tab is running an older one, asks the user to pull it. The backdrop
 * is deliberately not click-to-dismiss - "Later" is the explicit way out.
 */
const UpdatePrompt = () => {
  const { update, reloading, refresh, dismiss } = useVersionCheck();

  if (!update) return null;

  const { build, version, changes, alreadyReloaded } = update;

  return (
    <div className="update-prompt-backdrop" role="dialog" aria-modal="true" aria-labelledby="update-prompt-title">
      <div className="update-prompt">
        <div className="update-prompt__icon">
          <RefreshCw size={22} />
        </div>

        <h2 className="update-prompt__title" id="update-prompt-title">
          New version available
        </h2>

        <p className="update-prompt__subtitle">
          Build {build}
          {version ? ` (v${version})` : ''} is live - this tab is still on build{' '}
          {update.currentBuild}. Refresh to pull the latest changes.
        </p>

        {changes.length > 0 && (
          <div className="update-prompt__changes">
            <span className="update-prompt__changes-label">Recent changes</span>
            {changes.map((release) => (
              <ul key={release.build} className="update-prompt__changes-list">
                {(release.changes || []).map((change, index) => (
                  <li key={`${release.build}-${index}`}>{change}</li>
                ))}
              </ul>
            ))}
          </div>
        )}

        {alreadyReloaded && (
          <p className="update-prompt__warning">
            You already refreshed for this build and the server still reports a newer one - the
            new frontend may still be deploying. Try again in a minute.
          </p>
        )}

        <div className="update-prompt__actions">
          <Button variant="secondary" onClick={dismiss} disabled={reloading}>
            Later
          </Button>
          <Button variant="primary" onClick={refresh} loading={reloading}>
            {reloading ? 'Refreshing' : 'Refresh now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;
